import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PageHeader, QuickFilters, DataTable, Tag, Tooltip, Button, Dialog, Sheet, Field, Select, RadioList, Banner, Panel, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import { toHebrewNumeral } from '@NP-Webber/crm-kit';
import api from '../../services/api.js';
import ImportSectionNext from '../../components/ImportSectionNext.jsx';
import ExamCardNext from './ExamCardNext.jsx';
import { formatHebrewDate } from '../../utils/hebrewDate';
import { useQuickCreate } from '../../next/quickCreate';
import { MultiCheck, Row, apiError } from '../../next/ui';

/**
 * מבחנים — new design. Same endpoints as ExamsList: list, inline period change, detailed export (Excel / PDF / ZIP),
 * exam card (side sheet), exam + grade imports per period.
 */
const TYPE_LABELS = { time: 'זמן', shimur: 'שימור', miktzoa: 'מקצוע' };
const STATUS_LABELS = { draft: 'טיוטה', open: 'פתוח', closed: 'סגור', graded: 'צויין' };
const STATUS_TONES = { draft: 'neutral', open: 'success', closed: 'warning', graded: 'info' };
const periodName = (p, list = []) => {
  if (!p) return '';
  if (typeof p === 'object') return String(p.name || '').trim();
  return String(list.find((x) => String(x._id) === String(p))?.name || '').trim();
};
const formatSimanimRanges = (links) => (Array.isArray(links) ? links : []).map((link) => {
  const chelek = String(link?.chelek || '').trim();
  const ranges = (link?.ranges || []).map((r) => {
    const from = Number(r?.simanFrom);
    const to = Number(r?.simanTo);
    if (!from || !to) return '';
    return from === to ? toHebrewNumeral(from) : `${toHebrewNumeral(from)}-${toHebrewNumeral(to)}`;
  }).filter(Boolean);
  if (!chelek && !ranges.length) return '';
  return ranges.length ? `${chelek} סי' ${ranges.join(', ')}`.trim() : chelek;
}).filter(Boolean).join('; ');

function Clip({ text }) {
  const t = String(text || '').trim();
  if (!t) return <span className="ck-muted">—</span>;
  return t.length > 60 ? <Tooltip title={t}><span>{`${t.slice(0, 60)}…`}</span></Tooltip> : <span>{t}</span>;
}

const EXPORT_MODES = [
  { value: 'single', label: 'סיכום חומר לפי חלק (או״ח / יו״ד / אה״ע / חו״מ)', hint: 'קובץ אחד: ארבעת חלקי השו״ע וטווחי הסימנים בכל חלק — בלי כולל.' },
  { value: 'bySubject', label: 'סיכום חומר לפי מקצוע', hint: 'קובץ אחד: חלק (או״ח→יו״ד→אה״ע→חו״מ ברצף), ובתוך כל חלק — מקצועות וטווחי סימנים.' },
  { value: 'byKolel', label: 'ZIP לפי כולל — קובץ נפרד לכל כולל', hint: 'קוד/שם/עיר כולל, ולכל תקופה — קוד מבחן, שם מבחן וחומר נבחן. נכללים רק מבחנים עם קוד כולל.' },
  { value: 'byCity', label: 'ZIP לפי עיר — קובץ נפרד לכל עיר', hint: 'כמו לפי כולל, מקובץ לפי עיר.' },
];

export default function ExamsListNext() {
  const toast = useCkToast();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quick, setQuick] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  useQuickCreate(() => setSelectedId('new'));
  const [periods, setPeriods] = useState([]);
  const [importExamPeriodId, setImportExamPeriodId] = useState('');
  const [importGradePeriodId, setImportGradePeriodId] = useState('');
  const [exportOpen, setExportOpen] = useState(false);
  const [exportPeriodIds, setExportPeriodIds] = useState([]);
  const [exportMode, setExportMode] = useState('single');
  const [exportOrientation, setExportOrientation] = useState('landscape');
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    api.get('/exam-periods').then(({ data }) => {
      const list = data.data || [];
      setPeriods(list);
      const active = list.find((p) => p.isActive);
      if (active) { setImportExamPeriodId(active._id); setImportGradePeriodId(active._id); }
    }).catch(() => {});
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await api.get('/exams', { params: { page: 1, pageSize: 100000 } });
      setAllData((res.data || []).map((e) => ({ ...e, _periodName: periodName(e.periodId, periods), _simanimRange: formatSimanimRanges(e.libraryLinks) })));
    } catch (e) {
      setError(apiError(e, 'שגיאה בשליפת מבחנים'));
    } finally {
      setLoading(false);
    }
  }, [periods]);
  useEffect(() => { loadData(); }, [loadData]);

  const changePeriod = async (row, name) => {
    const target = periods.find((p) => p.name === name);
    const value = target?._id || null;
    try {
      const { data } = await api.put(`/exams/${row._id}`, { periodId: value, version: row.version });
      const populated = data.periodId && typeof data.periodId === 'object' ? data.periodId : (target || null);
      setAllData((prev) => prev.map((e) => (e._id === row._id ? { ...e, ...data, periodId: populated, _periodName: periodName(populated, periods), version: data.version } : e)));
      toast.success(`התקופה עודכנה${target ? ` ל«${target.name}»` : ''}`);
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בעדכון תקופה'));
    }
  };

  const openExport = () => {
    setExportPeriodIds([...periods].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((p) => String(p._id)));
    setExportMode('single');
    setExportOpen(true);
  };
  const download = (data, filename, mime) => {
    const url = URL.createObjectURL(new Blob([data], { type: mime }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };
  const exportError = async (err, fallback) => {
    try { const t = await err.response?.data?.text?.(); if (t) return JSON.parse(t)?.error || fallback; } catch { /* ignore */ }
    return fallback;
  };
  const runExport = async (pdf) => {
    if (!exportPeriodIds.length) { toast.error('יש לבחור לפחות מועד אחד'); return; }
    setExportLoading(true);
    try {
      const day = new Date().toISOString().slice(0, 10);
      const isZip = exportMode === 'byKolel' || exportMode === 'byCity';
      const response = await api.get(pdf ? '/exams/export-detailed-pdf' : '/exams/export-detailed', {
        params: { periodIds: exportPeriodIds.join(','), mode: exportMode, ...(pdf ? { orientation: exportOrientation } : {}) },
        responseType: 'blob',
      });
      const ext = isZip ? 'zip' : pdf ? 'pdf' : 'xlsx';
      const mime = isZip ? 'application/zip' : pdf ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const base = exportMode === 'byCity' ? 'מבחנים_מפורט_לפי_עיר' : exportMode === 'byKolel' ? 'מבחנים_מפורט_לפי_כולל' : exportMode === 'bySubject' ? 'סיכום_חומר_לפי_מקצוע' : 'סיכום_חומר';
      download(response.data, `${base}_${day}.${ext}`, mime);
      setExportOpen(false);
    } catch (err) {
      toast.error(await exportError(err, pdf ? 'שגיאה בייצוא PDF' : 'שגיאה בייצוא'));
    } finally {
      setExportLoading(false);
    }
  };

  const counts = useMemo(() => {
    const c = { all: allData.length };
    Object.keys(STATUS_LABELS).forEach((k) => { c[k] = allData.filter((e) => e.status === k).length; });
    return c;
  }, [allData]);
  const rows = useMemo(() => (quick === 'all' ? allData : allData.filter((e) => e.status === quick)), [allData, quick]);
  const periodOptions = periods.map((p) => ({ value: p._id, label: `${p.name}${p.isActive ? ' (פעיל)' : ''}` }));

  return (
    <>
      <PageHeader
        title="מבחנים"
        sub={`${allData.length.toLocaleString('he-IL')} מבחנים`}
        primary={{ label: 'מבחן חדש', onClick: () => setSelectedId('new') }}
        menus={[{ key: 'export', label: 'ייצוא', icon: 'download', items: [{ key: 'detailed', label: 'ייצוא מפורט (Excel / PDF / ZIP)', icon: 'download', onClick: openExport, disabled: !periods.length }] }]}
      />
      <QuickFilters
        value={quick}
        onChange={(k) => setQuick((c) => (c === k ? 'all' : k))}
        loading={loading && !allData.length}
        items={[
          { key: 'all', label: 'כל המבחנים', value: counts.all, icon: 'task', tone: 'neutral' },
          { key: 'open', label: 'פתוחים', value: counts.open, icon: 'play', tone: 'success' },
          { key: 'draft', label: 'טיוטות', value: counts.draft, icon: 'edit', tone: 'neutral' },
          { key: 'closed', label: 'סגורים', value: counts.closed, icon: 'pause', tone: 'warning' },
          { key: 'graded', label: 'צוינו', value: counts.graded, icon: 'check', tone: 'info' },
        ]}
      />
      <DataTable
        mode="client"
        rows={rows}
        rowKey="_id"
        loading={loading}
        error={error}
        onRetry={loadData}
        onRefresh={loadData}
        storageKey="bchinot-exams"
        layoutDefaults={{ pinned: ['name'], hidden: ['introText', 'graderNotes'] }}
        defaultQuery={{ sort: { key: 'code', dir: 1 } }}
        searchPlaceholder="חיפוש לפי קוד, שם, נושא, כולל או עיר…"
        exportFilename="מבחנים"
        emptyTitle="לא נמצאו מבחנים"
        onRowClick={(r) => setSelectedId(r._id)}
        rowActions={(r) => [{ key: 'edit', icon: 'edit', label: 'עריכה', onClick: () => setSelectedId(r._id) }]}
        columns={[
          { key: 'code', label: 'קוד', type: 'number', width: 80, render: (r) => <span className="nx-num">{r.code}</span> },
          { key: 'name', label: 'שם מבחן', width: 180, render: (r) => <b>{r.name}</b> },
          { key: 'type', label: 'סוג', type: 'enum', getValue: (r) => TYPE_LABELS[r.type] || r.type },
          { key: 'subject', label: 'נושא', type: 'enum' },
          { key: '_simanimRange', label: 'טווח סימנים', width: 200 },
          { key: 'date', label: 'תאריך', type: 'date', render: (r) => formatHebrewDate(r.date) || '—' },
          { key: 'status', label: 'סטטוס', type: 'enum', getValue: (r) => STATUS_LABELS[r.status] || r.status, render: (r) => <Tag tone={STATUS_TONES[r.status] || 'neutral'}>{STATUS_LABELS[r.status] || r.status}</Tag> },
          {
            key: '_periodName',
            label: 'תקופה',
            type: 'enum',
            getValue: (r) => r._periodName || 'ללא תקופה',
            render: (r) => (r._periodName ? <Tag tone={periods.find((p) => p.name === r._periodName)?.isActive ? 'primary' : 'neutral'} icon="calendar">{r._periodName}</Tag> : <span className="ck-muted">ללא תקופה</span>),
            editable: { options: [{ value: 'ללא תקופה', label: 'ללא תקופה' }, ...periods.map((p) => ({ value: p.name, label: `${p.name}${p.isActive ? ' (פעיל)' : ''}` }))], onSave: (row, v) => changePeriod(row, v) },
          },
          { key: 'kolelCode', label: 'קוד כולל', type: 'number' },
          { key: 'city', label: 'עיר', type: 'enum' },
          { key: 'introText', label: 'טקסט בתחילת המבחן', render: (r) => <Clip text={r.introText} /> },
          { key: 'graderNotes', label: 'הערות והארות (לבודק)', render: (r) => <Clip text={r.graderNotes} /> },
        ]}
      />

      <Panel title="ייבוא" icon="upload" sub="מבחנים וציונים — לפי מועד">
        <div className="nx-stack">
          <Row align="flex-end">
            <Field label="מועד לייבוא מבחנים"><Select value={importExamPeriodId} onChange={setImportExamPeriodId} placeholder="ללא תקופה" options={periodOptions} /></Field>
          </Row>
          <ImportSectionNext
            label="מבחנים"
            endpoint="/import/exams"
            templateEndpoint="/import/template/exams"
            description='עמודות חובה: קוד מבחן, שם מבחן, סוג, נושא, תאריך. שיוך לספרייה: חלק 1 + "מסימן 1 1" / "עד סימן 1 1" וכו׳ — עד 4 חלקים ו-4 רצפים לכל חלק. המועד נקבע לפי הבחירה למעלה.'
            onImportDone={() => loadData()}
            entityType="exams"
            identifierColumns={[{ field: 'code', label: 'קוד מבחן' }]}
            extraFormData={importExamPeriodId ? { periodId: importExamPeriodId } : undefined}
          />
          <Row align="flex-end">
            <Field label="מועד לייבוא ציונים"><Select value={importGradePeriodId} onChange={setImportGradePeriodId} placeholder="בחרו מועד" options={periodOptions} /></Field>
          </Row>
          <ImportSectionNext
            label="ציונים"
            endpoint="/import/grades"
            templateEndpoint="/import/template/grades"
            description="עמודות: קוד אישי, קוד מבחן, ציון. המועד נקבע לפי הבחירה למעלה."
            entityType="grades"
            extraFormData={importGradePeriodId ? { periodId: importGradePeriodId } : undefined}
            periodName={periods.find((p) => p._id === importGradePeriodId)?.name}
            uploadDisabled={!importGradePeriodId}
            uploadDisabledReason="יש לבחור מועד לפני ייבוא ציונים"
          />
        </div>
      </Panel>

      <Dialog
        open={exportOpen}
        onClose={() => !exportLoading && setExportOpen(false)}
        title="ייצוא מפורט"
        icon="download"
        size="md"
        footer={(
          <>
            <Button variant="ghost" disabled={exportLoading} onClick={() => setExportOpen(false)}>ביטול</Button>
            <Button icon="file" disabled={exportLoading || !exportPeriodIds.length} onClick={() => runExport(true)}>ייצוא PDF</Button>
            <Button variant="primary" icon="download" disabled={exportLoading || !exportPeriodIds.length} onClick={() => runExport(false)}>
              {exportLoading ? 'מייצא…' : (exportMode === 'single' || exportMode === 'bySubject') ? 'ייצוא Excel' : 'ייצוא'}
            </Button>
          </>
        )}
      >
        <div className="nx-stack">
          <Field label="תקופות">
            <MultiCheck value={exportPeriodIds} onChange={setExportPeriodIds} options={periods.map((p) => ({ value: String(p._id), label: `${p.name}${p.isActive ? ' (פעיל)' : ''}` }))} maxHeight={160} />
          </Field>
          <Row>
            <Button size="sm" variant="ghost" disabled={exportLoading} onClick={() => setExportPeriodIds(periods.map((p) => String(p._id)))}>הכל</Button>
            <Button size="sm" variant="ghost" disabled={exportLoading} onClick={() => setExportPeriodIds([])}>נקה</Button>
          </Row>
          <Field label="מצב ייצוא"><RadioList value={exportMode} onChange={setExportMode} disabled={exportLoading} options={EXPORT_MODES} /></Field>
          <Field label="כיוון דף (PDF)"><RadioList inline value={exportOrientation} onChange={setExportOrientation} disabled={exportLoading} options={[{ value: 'portrait', label: 'לאורך' }, { value: 'landscape', label: 'לרוחב' }]} /></Field>
          <Banner tone="info" icon="info">{EXPORT_MODES.find((m) => m.value === exportMode)?.hint}</Banner>
        </div>
      </Dialog>

      <Sheet open={!!selectedId} onClose={() => { setSelectedId(null); loadData(); }} width={1040} ariaLabel="כרטיס מבחן">
        {selectedId ? (
          <div className="nx-sheet-body">
            <ExamCardNext key={selectedId} recordId={selectedId} onClose={() => { setSelectedId(null); loadData(); }} />
          </div>
        ) : null}
      </Sheet>
    </>
  );
}
